import React, { useEffect, useState } from 'react';
import { InputField } from '../components/InputField';
import { DropdownField } from '../components/DropdownField';
import { FileUploadField } from '../components/FileUploadField';
import { TFormDataEmpresa, TGeographyData, TGeographyDataGroup, TRepresentantesRoles, TUserIdentityRepresentantes } from '../types/EmpresaTypes';
import FinametrixService from '../../../../services/FinametrixService';
import { PrimaryButton, Toggle } from '@fluentui/react';
import { RepresentantesRoleManagement } from '../components/RepresentantesRoleManagement';
import { validateFormData } from '../Utils/ValidationLegalInfoForm';

const initialState: TFormDataEmpresa = {
  razonSocial: '',
  taxId: '',
  paisEmisor: '',
  residente: 'Sí',
  fechaConstitucion: '',
  paisConstitucion: '',
  tomo: 0,
  folio: 0,
  hoja: 0,
  provincia: '',
  notario: '',
  protocolo: '',
  cnae: '',
  lei: '',
  sicav: '',
  email: '',
  documento: null,
  numMancomunados: 0,
  representantes: [],
  direccion: {
    tipoVia: '',
    nombreVia: '',
    numeroVia: '',
    piso: '',
    escalera: '',
    puerta: '',
    otrosDatos: '',
    paisDireccion: '',
    provinciaDireccion: '',
    ciudadDireccion: '',
    cpDireccion: '',
  }
};

const NUMERIC_FIELDS = ["tomo", "folio", "hoja", "numMancomunados"];

const BASE_CLASS = "legalInfoForm";

type TLegalInformProps = {
  userIdentitiesList: TUserIdentityRepresentantes[];
  submitData: (dataToSubmit: TFormDataEmpresa) => void;
}

export const LegalInfoForm: React.FC<TLegalInformProps> = ({ userIdentitiesList, submitData }) => {
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState<TFormDataEmpresa>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geographyData, setGeographyData] = useState<TGeographyDataGroup>({
    countries: [],
    regions: [],
    cities: [],
    postalCodes: [],
  });


  useEffect(() => {
    if (userIdentitiesList !== undefined) {
    const updateState: TFormDataEmpresa = {
      ...initialState,
      representantes: userIdentitiesList.map((userIdentities: TUserIdentityRepresentantes) => ({
            firstName: userIdentities.firstName ?? '',
            lastName: userIdentities.lastName ?? '',
            idDocument: userIdentities.idDocument ?? '',
            userIdentityId: userIdentities.id ?? 0,
            userId: userIdentities.userId ?? 0,
          }
      ))
     };
   
    setFormData(updateState);
    }
  }, [userIdentitiesList])

  useEffect(() => {
      FinametrixService.getCountries().then((res: TGeographyData[]) => {
        const paisesOrdenados = [...res].sort((a, b) => a.text.localeCompare(b.text, 'es'));
        setGeographyData(prev => ({
          ...prev,
          countries: paisesOrdenados,
        }));
      });
      FinametrixService.getRegions().then((res: TGeographyData[]) => {
        const regionesOrdenadas = [...res].sort((a, b) => a.text.localeCompare(b.text, 'es'));
        setGeographyData(prev => ({
          ...prev,
          regions: regionesOrdenadas,
        }));
      });
  }, []);

  useEffect(() => {
      const regionSelected = geographyData.regions.find(region => 
        region.text === formData.direccion.provinciaDireccion);
      if (regionSelected) {
        FinametrixService.getCitiesByRegion(regionSelected.value).then((res: TGeographyData[]) => {
          const ciudadesOrdenadas = [...res].sort((a, b) => a.text.localeCompare(b.text, 'es'));
          setGeographyData(prev => ({
            ...prev,
            cities: ciudadesOrdenadas,
          }));
        });
      }
  }, [formData.direccion.provinciaDireccion])

  useEffect(() => {
    FinametrixService.getPostalCodesByCity(formData.direccion.ciudadDireccion).then((postalCodesResult: TGeographyData[]) => {
        setGeographyData(prev => ({
          ...prev,
          postalCodes: postalCodesResult,
        }));
    })
  },[formData.direccion.ciudadDireccion])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const parsedValue = NUMERIC_FIELDS.includes(name) ? Number(value) : value;

    setFormData(prev =>
      name in prev.direccion
        ? { ...prev, direccion: { ...prev.direccion, [name]: parsedValue } }
        : { ...prev, [name]: parsedValue });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name in formData.direccion) {
      setFormData(prev => ({
        ...prev,
        direccion: {
          ...prev.direccion,
          [name]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const maxSizeMB = 5;

    if (!file) {
      setFormData({ ...formData, documento: null });
      setErrors({ ...errors, documento: '' });
      return;
    }

    // Validar tipo MIME
    if (file.type !== 'application/pdf') {
      setErrors({ ...errors, documento: 'El archivo debe ser un PDF.' });
      setFormData({ ...formData, documento: null });
      return;
    }

    // Validar tamaño
    if (file.size > maxSizeMB * 1024 * 1024) {
      setErrors({ ...errors, documento: `El archivo no debe superar los ${maxSizeMB}MB.` });
      setFormData({ ...formData, documento: null });
      return;
    }

    // Convertir a Base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result?.toString().split(',')[1]; 
      setFormData({ ...formData, documento: base64 || null });
      setErrors({ ...errors, documento: '' });
    };
    reader.readAsDataURL(file);
  };

  const handleRoleChange = (index: number, value: TRepresentantesRoles) => {
    const updated = [...formData.representantes];
    updated[index].rol = value;
    setFormData({ ...formData, representantes: updated });
  };

  const validate = () => {
    const newErrors = validateFormData(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setError('Hay un problema en el formulario. Resuélvalo para continuar.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setError('');
      submitData(formData);
    }
  };

  const onChangeToggle = (_: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    setFormData({ ...formData, "residente": checked ? 'Sí' : 'No' })
  }

  return (
    <form className={`${BASE_CLASS}`} onSubmit={handleSubmit}>
      <div className={`${BASE_CLASS}__form-section`}>
        <div className={`${BASE_CLASS}__form-row`}>
          <InputField label="Razón Social" name="razonSocial" type="text" value={formData.razonSocial} onChange={handleChange} error={errors.razonSocial} required />
          <InputField label="CIF / Tax ID" name="taxId" type="text" value={formData.taxId} onChange={handleChange} error={errors.taxId} required />
          <DropdownField label="País Emisor" name="paisEmisor" value={formData.paisEmisor} options={geographyData.countries} onChange={handleSelectChange} required />
          <InputField label="Fecha Constitución" name="fechaConstitucion" type="date" value={formData.fechaConstitucion} onChange={handleChange} required/>
          <DropdownField label="País Constitución" name="paisConstitucion" value={formData.paisConstitucion} options={geographyData.countries} onChange={handleSelectChange} required />
          <Toggle className={`${BASE_CLASS}__form-toggle`} label="Residente" defaultChecked onText="Sí" offText="No" onChange={onChangeToggle} />
        </div>
        <label className={`${BASE_CLASS}__form-section__label`}>Registro Mercantil</label>
        <div className={`${BASE_CLASS}__form-row`}>
          <InputField label="Tomo" name="tomo" type="number"  value={formData.tomo} onChange={handleChange} required />
          <InputField label="Folio" name="folio" type="number" value={formData.folio} onChange={handleChange} required />
          <InputField label="Hoja" name="hoja"  type="number" value={formData.hoja} onChange={handleChange} required />
          {/* <DropdownField label="Com. Autónoma / Estado" name="comAutonoma" value={formData.comAutonoma} options={regions} onChange={handleSelectChange} required /> */}
          <DropdownField label="Provincia" name="provincia" value={formData.provincia} options={geographyData.regions} onChange={handleSelectChange} required />
        </div>
        <div className={`${BASE_CLASS}__form-row`}>
            <InputField label="Nombre Notario" name="notario" type="text" value={formData.notario} onChange={handleChange} />
            <InputField label="Protocolo" name="protocolo" type="text" value={formData.protocolo} onChange={handleChange} required />
          </div>
        <div className={`${BASE_CLASS}__form-row`}>
          <InputField label="CNAE" name="cnae" type="text" value={formData.cnae} onChange={handleChange} required />
          <InputField label="Código LEI" name="lei" type="text" value={formData.lei} onChange={handleChange} required />
          <InputField label="Referencia SICAV" name="sicav" type="text" value={formData.sicav} onChange={handleChange} required />
        </div>
        <InputField label="Correo Electrónico" name="email" type="email" value={formData.email} onChange={handleChange} error={errors.email} required />
        <div className={`${BASE_CLASS}__form-row ${BASE_CLASS}__form-representante`}>
          <FileUploadField label="Documento de Constitución" name="documento" onChange={handleFileChange} required error={errors.documento} />
          <div className={`${BASE_CLASS}__representante__container`}>
            <InputField label="Número de mancomunados" name="numMancomunados" type="number" value={formData.numMancomunados} onChange={handleChange} error={errors.numApoderados} />
            <RepresentantesRoleManagement label="Representantes" integrantes={formData.representantes} onChange={handleRoleChange} error={errors.representantes} required />
          </div>
        </div>
        <label className={`${BASE_CLASS}__form-section__label`}>Dirección</label>
        <div className={`${BASE_CLASS}__form-row`}>
          <InputField label="Tipo de vía" name="tipoVia" type="text" value={formData.direccion.tipoVia} onChange={handleChange} error={errors.direccionTipoVia} required />
          <InputField label="Nombre de vía" name="nombreVia" type="text" value={formData.direccion.nombreVia} onChange={handleChange} error={errors.direccionNombreVia}  required />
          <InputField label="Piso" name="piso" type="text" value={formData.direccion.piso} onChange={handleChange} error={errors.direccionPiso}  required />
          <InputField label="Escalera" name="escalera" type="text" value={formData.direccion.escalera} onChange={handleChange} error={errors.direccionEscalera}  required />
          <InputField label="Puerta" name="puerta" type="text" value={formData.direccion.puerta} onChange={handleChange} error={errors.direccionPuerta} required />
          <InputField label="Otros datos" name="otrosDatos" type="text" value={formData.direccion.otrosDatos} onChange={handleChange} />
          <DropdownField label="País" name="paisDireccion" value={formData.direccion.paisDireccion} options={geographyData.countries} onChange={handleSelectChange} error={errors.direccionPais}  required />
          {/* <DropdownField label="Com. Autónoma / Estado" name="comAutonomaDireccion" value={formData.direccion.comAutonomaDireccion} options={regions} onChange={handleSelectChange} required /> */}
          <DropdownField label="Provincia" name="provinciaDireccion" value={formData.direccion.provinciaDireccion} options={geographyData.regions}  onChange={handleSelectChange} required />
          <DropdownField label="Ciudad" name="ciudadDireccion" value={formData.direccion.ciudadDireccion} options={geographyData.cities}  onChange={handleSelectChange} required />
          <DropdownField label="Código Postal" name="cpDireccion" value={formData.direccion.cpDireccion} options={geographyData.postalCodes}  onChange={handleSelectChange} required />
        </div>

        {error && <div className="invalid-feedback">{error}</div>}

        <div className={`${BASE_CLASS}__form-submit`}>
          <PrimaryButton type="submit">Enviar</PrimaryButton>
        </div>
      </div>
    </form>
  );
};